import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SchedoraLogo } from "@/components/schedora/SchedoraLogo";
import { useApp } from "@/context/AppContext";
import { supabase } from "@/lib/supabase";

interface InviteDetails {
  staff_member_id: string;
  business_id: string;
  email: string;
  staff_name: string;
  business_name: string;
}

export default function StaffAcceptInvite() {
  const { signup } = useApp();
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    const loadInvite = async () => {
      if (!token) {
        setError("This staff invitation link is missing its token.");
        setLoading(false);
        return;
      }

      const { data, error: nextError } = await supabase.rpc("get_staff_invitation", {
        invite_token: token,
      });

      if (nextError) {
        setError(nextError.message);
        setLoading(false);
        return;
      }

      const row = Array.isArray(data) ? data[0] : data;
      if (!row) {
        setError("This staff invitation is no longer available.");
        setLoading(false);
        return;
      }

      setInvite(row as InviteDetails);
      setLoading(false);
    };

    void loadInvite();
  }, [token]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!invite || !token) {
      setError("This invitation could not be loaded.");
      return;
    }

    if (password.length < 8) {
      setError("Use at least 8 characters for your password.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    const result = await signup({
      role: "staff",
      name: invite.staff_name,
      email: invite.email,
      password,
      businessId: invite.business_id,
      staffMemberId: invite.staff_member_id,
      invitationToken: token,
      emailRedirectTo: `${window.location.origin}/staff/dashboard`,
    });
    setIsSubmitting(false);

    if (!result.ok) {
      setError(result.message ?? "Unable to create your staff account.");
      return;
    }

    setMessage(result.message ?? "Check your inbox to confirm your staff account before signing in.");
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 gradient-hero items-center justify-center p-12">
        <div className="max-w-md text-center">
          <SchedoraLogo iconOnly iconClassName="h-12 w-12 mx-auto mb-6" />
          <h2 className="text-3xl font-heading font-bold text-foreground">Set up your staff account</h2>
          <p className="mt-3 text-muted-foreground">Finish your invite and unlock your own schedule, completed services, and commission dashboard.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-8 flex items-center gap-2 lg:hidden">
            <SchedoraLogo textClassName="text-xl" />
          </Link>
          <h1 className="text-2xl font-heading font-bold text-foreground">Accept staff invite</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {invite ? `${invite.staff_name} · ${invite.business_name}` : "Loading invitation details"}
          </p>

          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div>
              <Label>Email</Label>
              <Input value={invite?.email ?? ""} disabled className="mt-1" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Confirm Password</Label>
              <Input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} className="mt-1" />
            </div>
            {loading && <p className="text-sm text-muted-foreground">Loading invitation…</p>}
            {error && <p className="text-sm text-destructive">{error}</p>}
            {message && <p className="text-sm text-success">{message}</p>}
            <Button className="w-full" type="submit" disabled={loading || isSubmitting || !invite}>
              {isSubmitting ? "Creating Staff Account..." : "Create Staff Account"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
